/**
 * Cliente HTTP.
 *
 * Cuida de duas coisas que doeriam se estivessem espalhadas: renovar o token de
 * acesso quando ele expira e normalizar os erros do servidor num tipo só.
 *
 * A renovação usa uma promessa compartilhada. Sem isso, dez requisições
 * simultâneas encontrando um token expirado dispararia dez renovações, e como o
 * refresh token é rotativo, nove delas invalidariam as outras.
 */

import type {
  AdminOverview,
  AdminUserRow,
  Attachment,
  AuditLogEntry,
  BandwidthUsageReport,
  Category,
  Channel,
  Guild,
  GuildMember,
  Invite,
  Message,
  Role,
  SelfUser,
  UserSound,
  VoiceConnectionInfo,
  VoiceQualityLimits,
  VoiceRoomStat,
  VoiceState,
} from '@nexus/shared'

const BASE = import.meta.env.VITE_API_URL ?? ''
const ACCESS_TOKEN_KEY = 'nexus.accessToken'
const REFRESH_TOKEN_KEY = 'nexus.refreshToken'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** Erros de validação vêm com a lista de campos que falharam. */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {}
    const result: Record<string, string> = {}
    for (const item of this.details as { field?: string; message?: string }[]) {
      if (item.field && item.message) result[item.field] = item.message
    }
    return result
  }
}

class TokenStore {
  private accessToken: string | null = null
  private listeners = new Set<(token: string | null) => void>()

  constructor() {
    this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
  }

  get access(): string | null {
    return this.accessToken
  }

  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  }

  set(access: string, refresh?: string): void {
    this.accessToken = access
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
    this.listeners.forEach((fn) => fn(access))
  }

  clear(): void {
    this.accessToken = null
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    this.listeners.forEach((fn) => fn(null))
  }

  subscribe(fn: (token: string | null) => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }
}

export const tokens = new TokenStore()

let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const refreshToken = tokens.refresh
    const response = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    })

    if (!response.ok) {
      tokens.clear()
      throw new ApiError(401, 'session_expired', 'Sua sessão expirou. Entre novamente.')
    }

    const data = (await response.json()) as { accessToken: string; refreshToken: string }
    tokens.set(data.accessToken, data.refreshToken)
    return data.accessToken
  })()

  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Interno: evita laço infinito de renovação. */
  retried?: boolean
  skipAuth?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, retried, skipAuth, headers, ...rest } = options

  const isFormData = body instanceof FormData
  const finalHeaders: Record<string, string> = {
    ...(isFormData ? {} : { 'content-type': 'application/json' }),
    ...((headers as Record<string, string>) ?? {}),
  }

  if (!skipAuth && tokens.access) {
    finalHeaders.authorization = `Bearer ${tokens.access}`
  }

  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
    credentials: 'include',
    body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  })

  // Token expirado: renova uma vez e repete a requisição original.
  if (response.status === 401 && !retried && !skipAuth && tokens.refresh) {
    try {
      await refreshAccessToken()
      return request<T>(path, { ...options, retried: true })
    } catch {
      throw new ApiError(401, 'session_expired', 'Sua sessão expirou. Entre novamente.')
    }
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  let payload: unknown
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    payload = null
  }

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } })?.error
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown_error',
      error?.message ?? `Falha na requisição (${response.status})`,
      error?.details,
    )
  }

  return payload as T
}

const get = <T>(path: string) => request<T>(path)
const post = <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body })
const patch = <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body })
const put = <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body })
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' })

export interface AuthResponse {
  user: SelfUser
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface GuildPayload {
  guild: Guild
  channels: Channel[]
  categories: Category[]
  roles: Role[]
  members: GuildMember[]
}

export const api = {
  auth: {
    login: (identifier: string, password: string) =>
      post<AuthResponse>('/api/auth/login', { identifier, password }),

    register: (input: {
      username: string
      displayName: string
      email: string
      password: string
      inviteCode?: string
    }) => post<AuthResponse>('/api/auth/register', input),

    logout: () => post<{ ok: boolean }>('/api/auth/logout', { refreshToken: tokens.refresh }),

    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: boolean }>('/api/auth/change-password', { currentPassword, newPassword }),

    sessions: () =>
      get<{
        sessions: {
          id: string
          client: string
          userAgent: string | null
          ipAddress: string | null
          current: boolean
          createdAt: string
          lastUsedAt: string
        }[]
      }>('/api/auth/sessions'),
  },

  users: {
    me: () => get<{ user: SelfUser; presence: { status: string; customStatus: string | null } }>('/api/users/@me'),
    updateMe: (input: { displayName?: string; customStatus?: string | null; accentColor?: string }) =>
      patch<SelfUser>('/api/users/@me', input),
    uploadAvatar: (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return request<{ avatarUrl: string }>('/api/uploads/avatar', { method: 'POST', body: form })
    },
  },

  /** Bandeja pessoal de sons, a mesma em qualquer servidor. */
  sounds: {
    list: () => get<{ sounds: UserSound[] }>('/api/sounds'),
    upload: (file: File, name: string) => {
      const form = new FormData()
      form.append('name', name)
      form.append('file', file)
      return request<UserSound>('/api/uploads/sounds', { method: 'POST', body: form })
    },
    rename: (soundId: string, name: string) => patch<UserSound>(`/api/sounds/${soundId}`, { name }),
    remove: (soundId: string) => del<{ ok: boolean }>(`/api/sounds/${soundId}`),
    play: (soundId: string) => post<{ ok: boolean }>(`/api/sounds/${soundId}/play`),
  },

  guilds: {
    list: () => get<{ guilds: Guild[] }>('/api/guilds'),
    create: (input: { name: string; description?: string; template?: 'default' | 'empty' }) =>
      post<GuildPayload>('/api/guilds', input),
    detail: (guildId: string) => get<GuildPayload>(`/api/guilds/${guildId}`),
    update: (guildId: string, input: { name?: string; description?: string | null; iconUrl?: string | null }) =>
      patch<Guild>(`/api/guilds/${guildId}`, input),
    uploadIcon: (guildId: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return request<{ iconUrl: string; guild: Guild }>(`/api/uploads/guilds/${guildId}/icon`, {
        method: 'POST',
        body: form,
      })
    },
    remove: (guildId: string) => del<{ ok: boolean }>(`/api/guilds/${guildId}`),
    leave: (guildId: string) => post<{ ok: boolean }>(`/api/guilds/${guildId}/leave`),

    members: (guildId: string) => get<{ members: GuildMember[] }>(`/api/guilds/${guildId}/members`),
    updateMember: (guildId: string, userId: string, input: { nickname?: string | null; roleIds?: string[] }) =>
      patch<GuildMember>(`/api/guilds/${guildId}/members/${userId}`, input),
    kickMember: (guildId: string, userId: string) =>
      del<{ ok: boolean }>(`/api/guilds/${guildId}/members/${userId}`),

    roles: (guildId: string) => get<{ roles: Role[] }>(`/api/guilds/${guildId}/roles`),
    createRole: (guildId: string, input: { name: string; color?: string | null; permissions?: string }) =>
      post<Role>(`/api/guilds/${guildId}/roles`, input),
    updateRole: (
      guildId: string,
      roleId: string,
      input: { name?: string; color?: string | null; permissions?: string; hoist?: boolean },
    ) => patch<Role>(`/api/guilds/${guildId}/roles/${roleId}`, input),
    deleteRole: (guildId: string, roleId: string) =>
      del<{ ok: boolean }>(`/api/guilds/${guildId}/roles/${roleId}`),

    createCategory: (guildId: string, name: string) =>
      post<Category>(`/api/guilds/${guildId}/categories`, { name }),
    updateCategory: (guildId: string, categoryId: string, name: string) =>
      patch<Category>(`/api/guilds/${guildId}/categories/${categoryId}`, { name }),
    deleteCategory: (guildId: string, categoryId: string) =>
      del<{ ok: boolean }>(`/api/guilds/${guildId}/categories/${categoryId}`),
    createChannel: (
      guildId: string,
      input: {
        name: string
        type: 'text' | 'voice'
        categoryId?: string | null
        topic?: string
        userLimit?: number
      },
    ) => post<Channel>(`/api/guilds/${guildId}/channels`, input),
  },

  channels: {
    get: (channelId: string) => get<Channel>(`/api/channels/${channelId}`),
    update: (
      channelId: string,
      input: { name?: string; topic?: string | null; userLimit?: number; slowmodeSeconds?: number },
    ) => patch<Channel>(`/api/channels/${channelId}`, input),
    remove: (channelId: string) => del<{ ok: boolean }>(`/api/channels/${channelId}`),
    voiceParticipants: (channelId: string) =>
      get<{ participants: VoiceState[] }>(`/api/channels/${channelId}/voice-participants`),
  },

  messages: {
    list: (channelId: string, params: { limit?: number; before?: string; after?: string; around?: string } = {}) => {
      const query = new URLSearchParams()
      if (params.limit) query.set('limit', String(params.limit))
      if (params.before) query.set('before', params.before)
      if (params.after) query.set('after', params.after)
      if (params.around) query.set('around', params.around)
      const suffix = query.toString() ? `?${query}` : ''
      return get<{ messages: Message[] }>(`/api/channels/${channelId}/messages${suffix}`)
    },

    send: (
      channelId: string,
      input: { content: string; replyToId?: string | null; attachmentIds?: string[]; nonce?: string },
    ) => post<Message>(`/api/channels/${channelId}/messages`, input),

    edit: (channelId: string, messageId: string, content: string) =>
      patch<Message>(`/api/channels/${channelId}/messages/${messageId}`, { content }),

    remove: (channelId: string, messageId: string) =>
      del<{ ok: boolean }>(`/api/channels/${channelId}/messages/${messageId}`),

    react: (channelId: string, messageId: string, emoji: string) =>
      put<{ reactions: Message['reactions'] }>(
        `/api/channels/${channelId}/messages/${messageId}/reactions`,
        { emoji },
      ),

    unreact: (channelId: string, messageId: string, emoji: string) =>
      del<{ reactions: Message['reactions'] }>(
        `/api/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      ),

    pin: (channelId: string, messageId: string, pinned: boolean) =>
      put<Message>(`/api/channels/${channelId}/messages/${messageId}/pin`, { pinned }),

    pins: (channelId: string) => get<{ messages: Message[] }>(`/api/channels/${channelId}/pins`),

    uploadAttachment: (channelId: string, file: File, onProgress?: (percent: number) => void) => {
      // XMLHttpRequest em vez de fetch porque só ele reporta progresso de envio,
      // que é o que permite mostrar a barra num anexo grande.
      return new Promise<Attachment>((resolve, reject) => {
        const form = new FormData()
        form.append('file', file)

        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${BASE}/api/uploads/channels/${channelId}/attachments`)
        if (tokens.access) xhr.setRequestHeader('authorization', `Bearer ${tokens.access}`)
        xhr.withCredentials = true

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText) as Attachment)
          } else {
            let message = 'Falha ao enviar o arquivo'
            try {
              message = JSON.parse(xhr.responseText).error?.message ?? message
            } catch {
              // Resposta sem corpo JSON: fica a mensagem padrão.
            }
            reject(new ApiError(xhr.status, 'upload_failed', message))
          }
        }
        xhr.onerror = () => reject(new ApiError(0, 'network_error', 'Falha de rede ao enviar'))
        xhr.send(form)
      })
    },
  },

  invites: {
    preview: (code: string) =>
      request<{
        code: string
        guild: { id: string; name: string; iconUrl: string | null; description: string | null; memberCount: number }
        expiresAt: string | null
      }>(`/api/invites/${code}`, { skipAuth: true }),
    accept: (code: string) => post<{ guild: Guild; alreadyMember: boolean }>(`/api/invites/${code}/accept`),
    listForGuild: (guildId: string) => get<{ invites: Invite[] }>(`/api/invites/guild/${guildId}`),
    create: (guildId: string, input: { maxUses?: number; maxAgeSeconds?: number } = {}) =>
      post<Invite>(`/api/invites/guild/${guildId}`, input),
    revoke: (code: string) => del<{ ok: boolean }>(`/api/invites/${code}`),
  },

  voice: {
    join: (channelId: string) => post<VoiceConnectionInfo>(`/api/voice/${channelId}/join`),
    leave: (channelId: string) => post<{ ok: boolean }>(`/api/voice/${channelId}/leave`),
    limits: () => get<{ limits: VoiceQualityLimits }>('/api/voice/limits'),
    moderate: (
      channelId: string,
      input: { userId: string; serverMute?: boolean; serverDeaf?: boolean; disconnect?: boolean },
    ) => post<{ ok: boolean }>(`/api/voice/${channelId}/moderate`, input),
  },

  admin: {
    overview: (range = '24h') => get<AdminOverview>(`/api/admin/overview?range=${range}`),
    bandwidth: (range = '24h') => get<BandwidthUsageReport>(`/api/admin/bandwidth?range=${range}`),
    systemSeries: (range = '24h') =>
      get<{
        series: {
          bucket: string
          cpuPercent: number
          memoryMb: number
          eventLoopLagMs: number
          activeConnections: number
          activeVoiceParticipants: number
          messagesPerMinute: number
        }[]
      }>(`/api/admin/metrics/series?range=${range}`),
    voiceRooms: () => get<{ rooms: VoiceRoomStat[] }>('/api/admin/voice-rooms'),
    voiceUsage: (range = '7d') =>
      get<{
        usage: {
          userId: string
          username: string
          displayName: string
          sessions: number
          totalSeconds: number
          screenShareSeconds: number
          egressBytes: number
        }[]
      }>(`/api/admin/voice-usage?range=${range}`),
    users: (params: { search?: string; page?: number; pageSize?: number } = {}) => {
      const query = new URLSearchParams()
      if (params.search) query.set('search', params.search)
      if (params.page) query.set('page', String(params.page))
      if (params.pageSize) query.set('pageSize', String(params.pageSize))
      return get<{
        users: AdminUserRow[]
        page: number
        pageSize: number
        total: number
        localizacaoAtiva: boolean
      }>(
        `/api/admin/users?${query}`,
      )
    },
    updateUser: (userId: string, input: { isAdmin?: boolean; suspended?: boolean }) =>
      patch<{ user: unknown }>(`/api/admin/users/${userId}`, input),
    guilds: () =>
      get<{
        guilds: {
          id: string
          name: string
          iconUrl: string | null
          createdAt: string
          ownerId: string
          ownerName: string
          ownerUsername: string
          memberCount: number
          channelCount: number
          messageCount: number
        }[]
      }>('/api/admin/guilds'),
    settings: () =>
      get<{
        registrationMode: 'open' | 'invite_only' | 'closed'
        monthlyEgressQuotaBytes: number
        egressAlertPercent: number
        autoDowngradeQuality: boolean
        maxScreenShareBitrate: number
        maxScreenShareFramerate: number
        maxScreenShareHeight: number
        maxCameraBitrate: number
        updatedAt: string
      }>('/api/admin/settings'),
    updateSettings: (input: Record<string, unknown>) => patch<{ ok: boolean }>('/api/admin/settings', input),
    health: () =>
      get<{
        status: string
        services: { database: boolean; redis: boolean; livekit: boolean }
        uptimeSeconds: number
      }>('/api/admin/health'),
    audit: (limit = 50) => get<{ entries: AuditLogEntry[] }>(`/api/admin/audit?limit=${limit}`),
  },
}
